import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createTicket,
  getAllTickets,
  getTicket,
  getUnassignedTickets,
  mergeTicket,
  bulkAssignTickets,
  bulkModifyTickets,
  changeBillableStatus,
  updateTicketStatus,
  updateTicketPriority,
  updateTicketDetails,
  deleteTicketAttachments,
  updateScheduleConnectTime,
  getTicketQaStatus,
  addProductToTicket,
  addExpenseToTicket,
  updateTicketKnowledgeBase,
  addTicketKnowledgeBase,
  deleteTicketKnowledgeBase,
  getTicketCountByBoard,
  requestTicketFeedback,
} from "controller/api";
import { queryKeys, useTicketListCache } from "controller/cache";
import { toast } from "react-hot-toast";
import {
  AddTicketKnowledgeBaseInput,
  ApiErrorType,
  BulkAssignTicketInputProps,
  ChangeBillableStatusInput,
  CreateTicketInput,
  DeleteTicketAttachmentInput,
  DeleteTicketKnowledgeBaseInput,
  GetAllTicketQueryProps,
  GetQueryProps,
  GetUnassignedTicketsQueryProps,
  MergeTicketInput,
  UpdateAdditionalTicketDetailsInput,
  UpdateScheduleConnectTimeInput,
  UpdateTicketKnowledgeBaseInput,
  UpdateTicketPriorityInput,
  UpdateTicketStatusInput,
} from "shared";
import { addMixpanelEvent, debugConsole } from "utilz/helpers";
import { sentryLogger } from "utilz/logger/sentryLogger";

export const useGetAllTickets = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  widget,
  timeZone,
  interval,
  enabled = true,
}: GetAllTicketQueryProps & { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.tickets.list(filter, {
      pageNumber,
      searchQuery,
      sortColumn,
      sortDirection,
      perPageCount,
      widget,
      timeZone,
      interval,
    }),
    queryFn: () =>
      getAllTickets({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
        widget,
        timeZone,
        interval,
      }),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "getAllTickets",
      });
    },
    // cacheTime: Infinity,
    // staleTime: Infinity,
    enabled,
  });
};

// Dispatch portal — unassigned-ticket queue. Pagination (`pageNumber`/
// `perPageCount`), `searchQuery`, and board filtering (`boardIds`) are all
// honored by the backend. See the dispatch portal API validation report.
export const useGetUnassignedTickets = ({
  pageNumber,
  perPageCount,
  searchQuery,
  boardIds,
  priorities,
  ticketStatusIds,
  slaStatuses,
  sortColumn,
  sortDirection,
  enabled = true,
}: GetUnassignedTicketsQueryProps & { enabled?: boolean } = {}) => {
  const params = {
    pageNumber,
    perPageCount,
    searchQuery,
    boardIds,
    priorities,
    ticketStatusIds,
    slaStatuses,
    sortColumn,
    sortDirection,
  };
  return useQuery({
    // Every param is in the key, so changing a filter refetches rather than
    // serving the previous selection's rows.
    queryKey: ["unassigned_tickets", params],
    queryFn: () => getUnassignedTickets(params),
    // Keep the current list visible while a board/search change refetches, so the
    // queue doesn't flash empty between filters.
    keepPreviousData: true,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "getUnassignedTickets",
      });
    },
    enabled,
  });
};

export const useGetExportTickets = () => {
  return useMutation((value: GetQueryProps) => getAllTickets(value), {
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "getAllTickets",
      });
    },
  });
};

export const useGetTicket = (id?: string) => {
  return useQuery({
    queryKey: ["ticket", id],
    queryFn: () => getTicket(id || ""),
    enabled: id ? true : false,
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "getTicket",
      });
    },
  });
};

export const useCreateTicket = () => {
  const queryClient = useQueryClient();
  return useMutation((value: CreateTicketInput) => createTicket(value), {
    onSuccess(data, variables, context) {
      // queryClient.invalidateQueries(["tickets"]);
      queryClient.invalidateQueries(queryKeys.tickets.list());
      addMixpanelEvent({
        event: "ticket_created",
        data: variables,
      });
    },
    onError: (error: ApiErrorType, variables) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "createTicket",
      });
      addMixpanelEvent({
        event: "ticket_creation_failed",
        data: variables,
      });
    },
  });
};

export const useMergeTicket = () => {
  const queryClient = useQueryClient();
  return useMutation((value: MergeTicketInput) => mergeTicket(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["ticket", variables.parentTcketId]);
      queryClient.invalidateQueries(["tickets"]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "mergeTicket",
      });
    },
  });
};

export const useBulkAssignTicket = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: BulkAssignTicketInputProps) => bulkAssignTickets(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["tickets"]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "bulkAssignTickets",
        });
      },
    },
  );
};

export const useBulkModifyTickets = () => {
  const queryClient = useQueryClient();
  return useMutation((value: any) => bulkModifyTickets(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["tickets"]);
      // FIXME: use another api for single ticket update
      queryClient.invalidateQueries(["ticket", variables.ticket_ids[0]]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "bulkModifyTickets",
      });
    },
  });
};

export const useChangeBillableStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: ChangeBillableStatusInput) => changeBillableStatus(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "changeBillableStatus",
        });
      },
    },
  );
};

export const useUpdateTicketStatus = () => {
  const queryClient = useQueryClient();
  const { updateTicketListItemCache } = useTicketListCache();
  return useMutation(
    (value: UpdateTicketStatusInput) => updateTicketStatus(value),
    {
      onSuccess(data, variables, context) {
        updateTicketListItemCache(data);
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "updateTicketStatus",
        });
      },
    },
  );
};

export const useUpdateTicketPriority = () => {
  const queryClient = useQueryClient();
  const { updateTicketListItemCache } = useTicketListCache();
  return useMutation(
    (value: UpdateTicketPriorityInput) => updateTicketPriority(value),
    {
      onSuccess(data, variables, context) {
        updateTicketListItemCache(data);
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "updateTicketPriority",
        });
      },
    },
  );
};

export const useUpdateAdditionalDetails = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: UpdateAdditionalTicketDetailsInput) => updateTicketDetails(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },

      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "updateTicketDetails",
        });
      },
    },
  );
};

export const useDeleteTicketAttachments = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: DeleteTicketAttachmentInput) => deleteTicketAttachments(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "deleteTicketAttachments",
        });
      },
    },
  );
};
export const useUpdateScheduleConnectTime = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: UpdateScheduleConnectTimeInput) => updateScheduleConnectTime(value),
    {
      onSuccess(data, variables, context) {
        toast.success("Updated schedule connect time");
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "updateScheduleConnectTime",
        });
      },
    },
  );
};

export const useGetTicketQaStatus = (ticketId?: string) => {
  return useQuery({
    enabled: !!ticketId,
    queryKey: ["ticket", "ticketQaStatus", ticketId],
    queryFn: () => getTicketQaStatus(ticketId || ""),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "getTicketQaStatus",
      });
    },
  });
};

export const useAddProductToTicket = () => {
  const queryClient = useQueryClient();
  return useMutation((value: any) => addProductToTicket(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["ticket", variables.service_id]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "addProductToTicket",
      });
    },
  });
};

export const useAddExpenseToTicket = () => {
  const queryClient = useQueryClient();
  return useMutation((value: any) => addExpenseToTicket(value), {
    onSuccess(data, variables, context) {
      queryClient.invalidateQueries(["ticket", variables.service_id]);
    },
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "addExpenseToTicket",
      });
    },
  });
};

export const useAddTicketKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: AddTicketKnowledgeBaseInput) => addTicketKnowledgeBase(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "addTicketKnowledgeBase",
        });
      },
    },
  );
};

export const useUpdateTicketKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: UpdateTicketKnowledgeBaseInput) => updateTicketKnowledgeBase(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "updateTicketKnowledgeBase",
        });
      },
    },
  );
};

export const useDeleteTicketKnowledgeBase = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (value: DeleteTicketKnowledgeBaseInput) => deleteTicketKnowledgeBase(value),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticket", variables.ticketId]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "deleteTicketKnowledgeBase",
        });
      },
    },
  );
};

export const useGetTicketCountByBoard = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["ticket_count"],
    queryFn: () => getTicketCountByBoard(),
    onError: (error: ApiErrorType) => {
      sentryLogger({
        error: error,
        workflow: "ticket",
        functionName: "getTicketCountByBoard",
      });
    },
  });
};

export const useRequestTicketFeedback = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (ticketId: string) => requestTicketFeedback(ticketId),
    {
      onSuccess(data, variables, context) {
        queryClient.invalidateQueries(["ticket", variables]);
      },
      onError: (error: ApiErrorType) => {
        sentryLogger({
          error: error,
          workflow: "ticket",
          functionName: "requestTicketFeedback",
        });
      },
    },
  );
};
